/**
 * Compliance utilities for IRS Cash Tip reporting.
 * Handles the IRS standard 10th-day reporting rule, including shifting the deadline
 * to the next standard business day if it falls on a weekend or US federal holiday.
 */

function isWeekendOrFederalHoliday(date: Date): boolean {
  const dayOfWeek = date.getDay(); // 0 = Sunday, 6 = Saturday
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return true;
  }

  const year = date.getFullYear();
  const month = date.getMonth(); // 0-indexed (0 = Jan)
  const day = date.getDate();

  // Helper to find the nth occurrence of a day of week in a month
  const getNthDayOfWeek = (y: number, m: number, nth: number, dow: number): number => {
    let count = 0;
    for (let d = 1; d <= 31; d++) {
      const temp = new Date(y, m, d);
      if (temp.getMonth() !== m) break;
      if (temp.getDay() === dow) {
        count++;
        if (count === nth) return d;
      }
    }
    return -1;
  };

  // Helper to find the last occurrence of a day of week in a month
  const getLastDayOfWeek = (y: number, m: number, dow: number): number => {
    let lastDay = -1;
    for (let d = 1; d <= 31; d++) {
      const temp = new Date(y, m, d);
      if (temp.getMonth() !== m) break;
      if (temp.getDay() === dow) {
        lastDay = d;
      }
    }
    return lastDay;
  };

  // Helper to check fixed-date holidays with weekend observation shifting
  const isFixedHolidayObserved = (hMonth: number, hDay: number): boolean => {
    // Exact date matches
    if (month === hMonth && day === hDay) return true;

    // Saturday holiday observed on Friday
    const satHoliday = new Date(year, hMonth, hDay);
    if (satHoliday.getDay() === 6) {
      if (month === hMonth && day === hDay - 1 && dayOfWeek === 5) return true;
    }
    // Sunday holiday observed on Monday
    const sunHoliday = new Date(year, hMonth, hDay);
    if (sunHoliday.getDay() === 0) {
      if (month === hMonth && day === hDay + 1 && dayOfWeek === 1) return true;
    }

    return false;
  };

  // 1. New Year's Day (Jan 1)
  if (isFixedHolidayObserved(0, 1)) return true;

  // 2. Martin Luther King Jr. Day (3rd Monday of Jan)
  if (month === 0 && day === getNthDayOfWeek(year, 0, 3, 1)) return true;

  // 3. Washington's Birthday / Presidents' Day (3rd Monday of Feb)
  if (month === 1 && day === getNthDayOfWeek(year, 1, 3, 1)) return true;

  // 4. Memorial Day (Last Monday of May)
  if (month === 4 && day === getLastDayOfWeek(year, 4, 1)) return true;

  // 5. Juneteenth National Independence Day (June 19)
  if (isFixedHolidayObserved(5, 19)) return true;

  // 6. Independence Day (July 4)
  if (isFixedHolidayObserved(6, 4)) return true;

  // 7. Labor Day (1st Monday of Sep)
  if (month === 8 && day === getNthDayOfWeek(year, 8, 1, 1)) return true;

  // 8. Columbus Day (2nd Monday of Oct)
  if (month === 9 && day === getNthDayOfWeek(year, 9, 2, 1)) return true;

  // 9. Veterans Day (Nov 11)
  if (isFixedHolidayObserved(10, 11)) return true;

  // 10. Thanksgiving Day (4th Thursday of Nov)
  if (month === 10 && day === getNthDayOfWeek(year, 10, 4, 4)) return true;

  // 11. Christmas Day (Dec 25)
  if (isFixedHolidayObserved(11, 25)) return true;

  return false;
}

export function getIRSCashTipDeadline(year: number, month: number): Date {
  // IRS standard deadline is the 10th day of the following month
  let nextMonth = month + 1;
  let nextYear = year;
  if (nextMonth > 11) {
    nextMonth = 0;
    nextYear += 1;
  }

  // Set to the 10th day
  const deadline = new Date(nextYear, nextMonth, 10);

  // Shift to the next standard business day if it falls on a weekend or US federal holiday
  while (isWeekendOrFederalHoliday(deadline)) {
    deadline.setDate(deadline.getDate() + 1);
  }

  return deadline;
}

export function formatLongDate(date: Date): string {
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric'
  });
}
